/// @ts-check

import AddAccount from "./CustomersCreate/AddAccounts.jsx";
import { useState, useEffect } from "react";
import Card from "react-bootstrap/Card";
import "./Customers.css";
import ViewAccountsTable from "./ViewAccountsTable.jsx";
import UpdateVendor from "./components/UpdateVendor/UpdateVendor.jsx";
import TableLoader from "../../../components/Loader/TableLoader.jsx";
import NoDataFound from "../../../components/NoDataFound/NoDataFound.jsx";
import { getShippingData } from "./Api/Shared/SharedApi.js";
import AuditServices from "../../../Services/AuditsServices";
import usePermissions from "../../../components/CustomHooks/usePermissions.js";
import useUsers from "components/CustomHooks/useUsers";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getVendorAccounts } from "./api-v2/vendor-accounts";
import { PulseLoader } from "react-spinners";

const ViewOneVendor = () => {
  const { hasPermission } = usePermissions();
  const { getUsers } = useUsers();

  const urlParams = new URLSearchParams(window.location.search);
  const vendorID = urlParams.get("vendor");

  const [ShippingData, setShippingData] = useState([]);
  const [auditAccounts, setAuditAccounts] = useState([]);

  const [chargebackProcessorData, setChargebackProcessorData] = useState(
    /** @type {Array<{ label: string; value: string }> } */ ([]),
  );
  const [showAccountsForm, setShowAccountsForm] = useState(false);

  const vendorAccountsQuery = useQuery({
    enabled: vendorID != null,
    queryKey: /** @type {const} */ (["vendor-accounts", /** @type {string} */ (vendorID)]),
    queryFn: ({ queryKey: [, vendorId], signal }) => getVendorAccounts({ vendorId }, signal),
    placeholderData: keepPreviousData,
  });

  const refetchVendorAccounts = () => vendorAccountsQuery.refetch();

  const GetAuditAccounts = async () => {
    try {
      let response = await AuditServices.GetAuditAccounts();
      if (response.status === 200) {
        setAuditAccounts(
          response.data.accounts?.map((/** @type {[string, meta: { status: string }]} */ val) => ({
            label: val[0],
            value: val[0],
            status: val[1].status,
          })),
        );
      }
    } catch (e) {
      /* empty */
    }
  };

  useEffect(() => {
    GetAuditAccounts();
    getProcessors("chargebacks");
    getShippingData(setShippingData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowAccountsForm = () => {
    setShowAccountsForm(!showAccountsForm);
  };

  const getProcessors = (/** @type {string} */ type) => {
    if (typeof type !== "string") {
      throw new Error("Invalid processor type");
    }

    type = type.toLowerCase();

    if (!new Set(["chargebacks", "shortages"]).has(type)) {
      throw new Error("Invalid processor type");
    }

    /// @ts-ignore
    getUsers(undefined, undefined, undefined, [
      "admin:*",
      "user:" + type + ":write:*",
      "user:" + type + ":read:*",
    ]).then(response => {
      const users = response?.data?.users;
      if (!users || !Array.isArray(users)) {
        console.error("Invalid users data", response);
        return;
      }

      const convertedData = users.map((/** @type {{ NICKNAME: string; ID: string; }} */ item) => ({
        label: item.NICKNAME,
        value: item.ID,
      }));

      convertedData.unshift({
        label: "Select " + (type.charAt(0).toUpperCase() + type.slice(1)) + " Processor",
        // HACK: existing code uses null to indicate no selection
        value: /** @type {string} */ (/** @type {unknown} */ (null)),
      });
      switch (type) {
        case "chargebacks":
          setChargebackProcessorData(convertedData);
          break;
      }
    });
  };

  return (
    <>
      <Card
        className="rounded bg_shadow bg-light-subtle"
        style={{ width: "95%", marginTop: "10px", marginBottom: "10px" }}
      >
        <Card.Header>Update Customer</Card.Header>
        <Card.Body>
          <UpdateVendor vendorID={vendorID} />
        </Card.Body>
      </Card>
      <Card
        className="rounded bg_shadow bg-light-subtle"
        style={{ width: "95%", marginTop: "10px", marginBottom: "10px" }}
      >
        <Card.Header>Stores</Card.Header>
        {hasPermission(["admin:*", "user:client_config:write:*"]) && (
          <div className="mt-5">
            <button
              className="btn btn-dark"
              onClick={() => {
                handleShowAccountsForm();
              }}
            >
              Add Store
            </button>

            <div>
              {showAccountsForm && (
                <AddAccount
                  vendorId={vendorID}
                  chargebackProcessorData={chargebackProcessorData}
                  ShippingData={ShippingData}
                  getData={refetchVendorAccounts}
                  handleShowAccountsForm={handleShowAccountsForm}
                  avcData={auditAccounts}
                  tableDataEdit={undefined}
                />
              )}
            </div>
          </div>
        )}
        <div className="container-sm table_main_account">
          <Card.Body>
            {vendorAccountsQuery.isRefetching && (
              <div
                style={{
                  position: "sticky",
                  top: "4rem",
                  marginInline: "auto",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                  backgroundColor: "white",
                  padding: "10px",
                  border: "1px solid #ccc",
                  boxShadow: "rgba(0, 0, 0, 0.2) 0 2px 16px",
                  borderRadius: "8px",
                  zIndex: 1,
                }}
              >
                <PulseLoader size="8px" color="#EF1442" />
                <span>Refreshing list of accounts...</span>
              </div>
            )}
            {!vendorAccountsQuery.isPending ? (
              <div {...(vendorAccountsQuery.isRefetching && { style: { opacity: 0.5 }, inert: "" })}>
                {vendorAccountsQuery.data?.length ? (
                  <ViewAccountsTable
                    auditAccounts={auditAccounts}
                    chargebackProcessorData={chargebackProcessorData}
                    refetchVendorAccounts={refetchVendorAccounts}
                    ShippingData={ShippingData}
                    vendorAccounts={vendorAccountsQuery.data}
                    isRefreshing={vendorAccountsQuery.isPending}
                  />
                ) : (
                  <NoDataFound />
                )}
              </div>
            ) : (
              <TableLoader />
            )}
          </Card.Body>
        </div>
      </Card>
    </>
  );
};

export default ViewOneVendor;
